import {
  onIdTokenChanged,
  signInWithEmailAndPassword,
  signOut,
  type Auth,
  type User,
} from 'firebase/auth';
import { doc, onSnapshot, type Firestore } from 'firebase/firestore';
import {
  resolvePermissions,
  standardRoleMap,
  type Permission,
  type RoleDefinition,
} from '@ecobox/domain';
import { createStore, type ReadableStore } from './store';

export const STAFF_EMAIL_DOMAIN = 'staff.eco-box.app';

export type ClaimScope =
  'all' | 'doc' | { sectors: readonly string[]; branches: readonly string[] };

export interface StaffClaims {
  staff: true;
  active: boolean;
  roleIds: readonly string[];
  scope: ClaimScope;
}

export interface StaffProfile {
  username: string;
  displayName: string;
  roleIds: readonly string[];
  scope: ClaimScope;
  active: boolean;
  permsVersion: number;
  /** Signed in with a temporary password: choose your own before anything else (07 §7.2). */
  mustChangePassword?: boolean;
  /** The account behind an AI worker connection (no sign-in); people have no kind. */
  kind?: 'bot';
}

const USERNAME_RE = /^[a-z0-9][a-z0-9._-]{1,31}$/;

export function normalizeUsername(username: string): string {
  return username.trim().toLowerCase();
}

export function isValidUsername(username: string): boolean {
  return USERNAME_RE.test(normalizeUsername(username));
}

export function usernameToEmail(username: string): string {
  return `${normalizeUsername(username)}@${STAFF_EMAIL_DOMAIN}`;
}

// ---- sign in ---------------------------------------------------------------

export type StaffAuthErrorCode =
  | 'invalid_username'
  | 'invalid_credentials'
  | 'too_many_attempts'
  | 'account_disabled'
  | 'offline'
  | 'not_staff'
  | 'inactive'
  | 'unknown';

export class StaffAuthError extends Error {
  readonly code: StaffAuthErrorCode;
  constructor(code: StaffAuthErrorCode, message: string) {
    super(message);
    this.name = 'StaffAuthError';
    this.code = code;
  }
}

function mapAuthError(err: unknown): StaffAuthError {
  const code =
    typeof err === 'object' && err !== null && 'code' in err
      ? String((err as { code: unknown }).code)
      : '';
  switch (code) {
    case 'auth/invalid-credential':
    case 'auth/wrong-password':
    case 'auth/user-not-found':
    case 'auth/invalid-email':
      return new StaffAuthError('invalid_credentials', 'Wrong username or password.');
    case 'auth/too-many-requests':
      return new StaffAuthError(
        'too_many_attempts',
        'Too many attempts. Wait a few minutes and try again.',
      );
    case 'auth/user-disabled':
      return new StaffAuthError('account_disabled', 'This account is disabled. Ask your manager.');
    case 'auth/network-request-failed':
      return new StaffAuthError('offline', 'No connection. Sign-in needs the internet.');
    default:
      return new StaffAuthError('unknown', 'Sign-in failed. Try again.');
  }
}

/** Username + password. The username becomes `{username}@staff.eco-box.app` (07 §7.1). */
export async function signInStaff(auth: Auth, username: string, password: string): Promise<User> {
  if (!isValidUsername(username)) {
    throw new StaffAuthError(
      'invalid_username',
      'Usernames are 2–32 letters, numbers, dots, dashes or underscores.',
    );
  }
  if (password.length === 0)
    throw new StaffAuthError('invalid_credentials', 'Enter your password.');
  let user: User;
  try {
    user = (await signInWithEmailAndPassword(auth, usernameToEmail(username), password)).user;
  } catch (err) {
    throw mapAuthError(err);
  }
  const claims = await readClaims(user);
  if (!claims) {
    await signOut(auth);
    throw new StaffAuthError('not_staff', 'This account is not a staff account.');
  }
  if (!claims.active) {
    await signOut(auth);
    throw new StaffAuthError('inactive', 'This staff account is not active. Ask your manager.');
  }
  return user;
}

// ---- claims ----------------------------------------------------------------

function stringArray(v: unknown): string[] | null {
  return Array.isArray(v) && v.every((x) => typeof x === 'string') ? (v as string[]) : null;
}

function parseScope(v: unknown): ClaimScope | null {
  if (v === 'all' || v === 'doc') return v;
  if (typeof v === 'object' && v !== null) {
    const sectors = stringArray((v as Record<string, unknown>).sectors);
    const branches = stringArray((v as Record<string, unknown>).branches);
    if (sectors && branches) return { sectors, branches };
  }
  return null;
}

export function parseStaffClaims(raw: unknown): StaffClaims | null {
  if (typeof raw !== 'object' || raw === null) return null;
  const c = raw as Record<string, unknown>;
  if (c.staff !== true) return null;
  const roleIds = stringArray(c.roleIds) ?? [];
  const scope = parseScope(c.scope) ?? { sectors: [], branches: [] };
  return { staff: true, active: c.active === true, roleIds, scope };
}

export function parseStaffProfile(raw: unknown): StaffProfile | null {
  if (typeof raw !== 'object' || raw === null) return null;
  const p = raw as Record<string, unknown>;
  const roleIds = stringArray(p.roleIds);
  const scope = parseScope(p.scope);
  if (!roleIds || !scope || typeof p.username !== 'string') return null;
  return {
    username: p.username,
    displayName: typeof p.displayName === 'string' ? p.displayName : p.username,
    roleIds,
    scope,
    active: p.active === true,
    permsVersion: typeof p.permsVersion === 'number' ? p.permsVersion : 0,
    mustChangePassword: p.mustChangePassword === true,
    ...(p.kind === 'bot' ? { kind: 'bot' as const } : {}),
  };
}

/** Read a JWT's payload without any network call. */
export function decodeJwtClaims(token: string): Record<string, unknown> | null {
  const part = token.split('.')[1];
  if (!part) return null;
  try {
    const b64 = part.replace(/-/g, '+').replace(/_/g, '/');
    const json = decodeURIComponent(
      atob(b64.padEnd(Math.ceil(b64.length / 4) * 4, '='))
        .split('')
        .map((ch) => `%${ch.charCodeAt(0).toString(16).padStart(2, '0')}`)
        .join(''),
    );
    const parsed: unknown = JSON.parse(json);
    return typeof parsed === 'object' && parsed !== null
      ? (parsed as Record<string, unknown>)
      : null;
  } catch (err) {
    console.warn('Token could not be decoded.', err);
    return null;
  }
}

function cachedAccessToken(user: User): string | null {
  const json = user.toJSON() as { stsTokenManager?: { accessToken?: unknown } };
  const token = json.stsTokenManager?.accessToken;
  return typeof token === 'string' ? token : null;
}

/**
 * Claims from the ID token, never forcing a refresh. If the token is expired and the device is
 * offline (so the SDK cannot renew it), fall back to the claims inside the stored token: the
 * ops app opens offline with the cached session (07 §7.1).
 */
export async function readClaims(user: User): Promise<StaffClaims | null> {
  try {
    return parseStaffClaims((await user.getIdTokenResult()).claims);
  } catch (err) {
    console.warn('Using the cached token claims.', err);
    const token = cachedAccessToken(user);
    return token ? parseStaffClaims(decodeJwtClaims(token)) : null;
  }
}

const ROLES: ReadonlyMap<string, RoleDefinition> = standardRoleMap();

export function permissionsFromClaims(
  claims: Pick<StaffClaims, 'active' | 'roleIds'> | null,
  roles: ReadonlyMap<string, RoleDefinition> = ROLES,
): ReadonlySet<Permission> {
  if (!claims?.active) return new Set();
  return resolvePermissions(claims.roleIds, roles);
}

/** Branch codes the claims allow the person to work at, or null for "any". */
export function scopeBranches(scope: ClaimScope | undefined): readonly string[] | null {
  if (scope === undefined || scope === 'all' || scope === 'doc') return null;
  return scope.branches;
}

// ---- session store ---------------------------------------------------------

export interface StaffSessionState {
  status: 'loading' | 'signed_out' | 'signed_in';
  uid: string | null;
  claims: StaffClaims | null;
  profile: StaffProfile | null;
  /** Signed in with the staff claim and active. */
  isStaff: boolean;
}

export interface StaffSession extends ReadableStore<StaffSessionState> {
  start(): void;
  dispose(): void;
}

const LOADING: StaffSessionState = {
  status: 'loading',
  uid: null,
  claims: null,
  profile: null,
  isStaff: false,
};

function sameSet(a: readonly string[], b: readonly string[]): boolean {
  return a.length === b.length && [...a].sort().join('\u0000') === [...b].sort().join('\u0000');
}

function perms(profile: StaffProfile): string {
  return `${profile.permsVersion}|${profile.active}|${[...profile.roleIds].sort().join(',')}`;
}

export function createStaffSession(deps: { auth: Auth; db: Firestore }): StaffSession {
  const { auth, db } = deps;
  const store = createStore<StaffSessionState>(LOADING);
  const cleanups: (() => void)[] = [];
  let user: User | null = null;
  let stopProfile: (() => void) | undefined;
  /** The profile state the current token is known to match (or to have been refreshed for). */
  let settledKey: string | undefined;
  let refreshing = false;

  const setClaims = (claims: StaffClaims | null): void => {
    const cur = store.getSnapshot();
    store.set({ ...cur, claims, isStaff: claims?.active === true });
  };

  const maybeRefresh = async (): Promise<void> => {
    const { profile, claims } = store.getSnapshot();
    if (!user || !profile || refreshing) return;
    const key = perms(profile);
    if (settledKey === undefined) {
      const consistent =
        claims !== null &&
        claims.active === profile.active &&
        sameSet(claims.roleIds, profile.roleIds);
      if (consistent) {
        settledKey = key;
        return;
      }
    } else if (settledKey === key) {
      return;
    }
    if (typeof navigator !== 'undefined' && !navigator.onLine) return;
    refreshing = true;
    try {
      await user.getIdToken(true);
      setClaims(await readClaims(user));
      settledKey = key;
    } catch (err) {
      console.warn('Token refresh after a permissions change failed; will retry.', err);
    } finally {
      refreshing = false;
    }
  };

  const watchProfile = (uid: string): void => {
    stopProfile?.();
    stopProfile = onSnapshot(
      doc(db, 'staff', uid),
      (snap) => {
        const profile = snap.exists() ? parseStaffProfile(snap.data()) : null;
        store.set({ ...store.getSnapshot(), profile });
        void maybeRefresh();
      },
      (err) => console.warn('Staff profile could not be read.', err),
    );
  };

  const onOnline = (): void => {
    void maybeRefresh();
  };

  return {
    subscribe: store.subscribe,
    getSnapshot: store.getSnapshot,
    start() {
      if (cleanups.length > 0) return;
      cleanups.push(
        onIdTokenChanged(auth, (next) => {
          const changedUser = next?.uid !== user?.uid;
          user = next;
          if (!next) {
            stopProfile?.();
            stopProfile = undefined;
            settledKey = undefined;
            store.set({ ...LOADING, status: 'signed_out' });
            return;
          }
          if (changedUser) {
            settledKey = undefined;
            store.set({
              status: 'signed_in',
              uid: next.uid,
              claims: null,
              profile: null,
              isStaff: false,
            });
            watchProfile(next.uid);
          }
          void readClaims(next).then((claims) => {
            if (user?.uid === next.uid) {
              setClaims(claims);
              void maybeRefresh();
            }
          });
        }),
      );
      if (typeof window !== 'undefined') {
        window.addEventListener('online', onOnline);
        cleanups.push(() => window.removeEventListener('online', onOnline));
      }
      cleanups.push(() => stopProfile?.());
    },
    dispose() {
      for (const c of cleanups.splice(0)) c();
    },
  };
}
